import type { FC } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { PaginationComponentProps } from "react-data-table-component";

export type DataTablePaginationConfig = {
  readonly rowsPerPageOptions?: readonly number[];
  /** Narrow sidebar layout: prev/next + short range only (no page number pills). */
  readonly variant?: "full" | "compact";
};

type PageSegment =
  | { readonly kind: "page"; readonly page: number }
  | { readonly kind: "ellipsis"; readonly key: "gap-start" | "gap-end" };

function buildPageList(current: number, total: number): readonly PageSegment[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => ({
      kind: "page" as const,
      page: i + 1
    }));
  }
  const delta = 1;
  const segments: PageSegment[] = [];
  const left = Math.max(2, current - delta);
  const right = Math.min(total - 1, current + delta);
  segments.push({ kind: "page", page: 1 });
  if (left > 2) {
    segments.push({ kind: "ellipsis", key: "gap-start" });
  }
  for (let i = left; i <= right; i++) {
    segments.push({ kind: "page", page: i });
  }
  if (right < total - 1) {
    segments.push({ kind: "ellipsis", key: "gap-end" });
  }
  segments.push({ kind: "page", page: total });
  return segments;
}

const navBtnClass =
  "inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-all duration-200 ease-in-out hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white";

const pageBtnBase =
  "flex h-8 min-w-8 items-center justify-center rounded border px-2 text-sm font-medium transition-all duration-200 ease-in-out";

export function createDataTablePagination(
  config: DataTablePaginationConfig = {}
): FC<PaginationComponentProps> {
  const { rowsPerPageOptions, variant = "full" } = config;

  const DataTablePagination: FC<PaginationComponentProps> = ({
    rowsPerPage,
    rowCount,
    currentPage,
    onChangePage,
    onChangeRowsPerPage
  }) => {
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    const start = rowCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, rowCount);
    const isFirstPage = currentPage <= 1;
    const isLastPage = currentPage >= totalPages;

    if (variant === "compact") {
      const countText =
        rowCount === 0 ? "0 of 0" : `${start}–${end} of ${rowCount}`;
      return (
        <div className="flex flex-nowrap items-center justify-between gap-2 border-t border-gray-200 bg-white px-3 py-2">
          <span className="min-w-0 shrink-0 text-xs text-gray-500">
            {countText}
          </span>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => onChangePage(currentPage - 1, rowCount)}
              disabled={isFirstPage}
              aria-label="Previous page"
              className={navBtnClass}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => onChangePage(currentPage + 1, rowCount)}
              disabled={isLastPage}
              aria-label="Next page"
              className={navBtnClass}
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      );
    }

    const pageList = buildPageList(currentPage, totalPages);

    return (
      <div className="flex w-full flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <span className="text-sm text-gray-500">
            Showing {rowCount === 0 ? 0 : start} to {end} of {rowCount}{" "}
            results
          </span>
          {rowsPerPageOptions && rowsPerPageOptions.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-500">
              <span className="sr-only">Rows per page</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  onChangeRowsPerPage(next, currentPage);
                }}
                className="rounded border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                {rowsPerPageOptions.map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onChangePage(currentPage - 1, rowCount)}
            disabled={isFirstPage}
            aria-label="Previous page"
            className={navBtnClass}
          >
            <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
            <span>Previous</span>
          </button>

          <div className="flex flex-wrap items-center gap-1">
            {pageList.map((segment) =>
              segment.kind === "ellipsis" ? (
                <span
                  key={segment.key}
                  className="px-1 text-sm text-gray-400"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <button
                  key={segment.page}
                  type="button"
                  onClick={() => onChangePage(segment.page, rowCount)}
                  aria-label={`Page ${segment.page}`}
                  aria-current={
                    segment.page === currentPage ? "page" : undefined
                  }
                  className={
                    segment.page === currentPage
                      ? `${pageBtnBase} border-primary-500 bg-primary-500 text-white`
                      : `${pageBtnBase} border-gray-200 bg-white text-primary-600 hover:bg-gray-50`
                  }
                >
                  {segment.page}
                </button>
              )
            )}
          </div>

          <button
            type="button"
            onClick={() => onChangePage(currentPage + 1, rowCount)}
            disabled={isLastPage}
            aria-label="Next page"
            className={navBtnClass}
          >
            <span>Next</span>
            <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
          </button>
        </div>
      </div>
    );
  };

  DataTablePagination.displayName = "DataTablePagination";
  return DataTablePagination;
}
